import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData, IApplicationProperty } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

/**
 * Reads and writes a single field on a specific property (by GUID) in the React Query cache.
 *
 * Replaces the `const [x, set_x] = useRecoilState(someAtomFamily(propertyGuid))` pattern.
 *
 * @param propertyGuid - GUID of the property to read/write
 * @param selector     - reads the field value from IApplicationProperty
 * @param updater      - returns a new IApplicationProperty with the field updated
 * @returns            [currentValue, setter]
 *
 * @example
 * const [street1, set_street1] = usePropertyField(
 *     propertyGuid,
 *     (p) => p.address?.street1,
 *     (p, v) => ({ ...p, address: { ...p.address, street1: v } })
 * );
 */
export function usePropertyField<T>(
    propertyGuid: string,
    selector: (property: IApplicationProperty) => T,
    updater: (property: IApplicationProperty, value: T) => IApplicationProperty
): [T | undefined, (value: T) => void] {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<IApplicationData, Error, T | undefined>({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
        select: (d) => {
            const property = d.information?.properties?.items?.find((p) => p.guid === propertyGuid);
            return property !== undefined ? selector(property) : undefined;
        },
    });

    const setValue = (value: T) => {
        if (entryGuid === undefined) return;
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid), (old) => {
            if (!old) return old;
            const items = old.information?.properties?.items ?? [];
            const idx = items.findIndex((p) => p.guid === propertyGuid);
            if (idx === -1) return old;
            const newItems = [...items];
            newItems[idx] = updater(newItems[idx], value);
            return {
                ...old,
                information: {
                    ...old.information,
                    properties: {
                        ...old.information.properties,
                        items: newItems,
                    },
                },
            };
        });
    };

    return [currentValue, setValue];
}
